import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { t } from '../../../i18n'
import axios from 'axios'
import map from 'lodash/map'
import uniq from 'lodash/uniq'
import debounce from 'lodash/debounce'
import Select from '@mui/material/Select'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import lowerFirst from 'lodash/lowerFirst'
import moment, { Moment } from 'moment-timezone'
import { LocalizationContext, useTenantTimezone } from '../../shared/contexts'
import TenantDateTimePicker from '../../shared/TenantDateTimePicker'
import { tenantNow } from '../../../utils/tenantDateTime'
import { ListItemText, MenuItem, OutlinedInput, Pagination } from '@mui/material'
import { getParam, applyParams, parseIsoParam, parsePageParam, parseSearchParams } from './urlParams'
import { available_commands_and_steps_admin_command_step_durations_path } from '../../../../routes'
import { TIME_SOURCE, timeSourceTitle } from '../../../utils/timeSource'
import ExportToExcelButton from './ExportToExcelButton'

const scope = 'command_history'

type SearchOption = 'queued_order_id'

type CommandStepSearchOption = SearchOption | 'command_name' | 'step_name'
const commandStepSearchOptions = [{
  label: t('command_name', { scope }),
  value: 'command_name' as CommandStepSearchOption
}, {
  label: t('step_name', { scope }),
  value: 'step_name' as CommandStepSearchOption
}, {
  label: t('queued_order_id', { scope }),
  value: 'queued_order_id' as CommandStepSearchOption
}];

class CommandStepSearch {
  attribute: CommandStepSearchOption
  value: string | undefined
}

type CommandSearchOption = SearchOption | 'command_name'
const commandSearchOptions = [{
  label: t('command_name', { scope }),
  value: 'command_name' as CommandSearchOption
}, {
  label: t('queued_order_id', { scope }),
  value: 'queued_order_id' as CommandSearchOption
}];

type QueuedOrderSearchOption = SearchOption | 'recipe_name'
const queuedOrderSearchOptions = [{
  label: t('recipe_name', { scope }),
  value: 'recipe_name' as CommandStepSearchOption
}, {
  label: t('queued_order_id', { scope }),
  value: 'queued_order_id' as CommandStepSearchOption
}];

class QueuedOrderSearch {
  attribute: QueuedOrderSearchOption
  value: string | undefined
}

class PageOptions {
  searchAttribute: CommandStepSearchOption
  searchValue: string
  currentPage: number
  grouping: 'command_step_durations' | 'command_durations' | 'queued_order_durations'
  timeFrom: Moment | null
  timeFromIsAutoDefault: boolean
  timeUntil: Moment | null
}

const findSearchAttribute = (groupingType) => {
  switch (groupingType) {
    case 'command_step_durations':
      return 'step_name'
    case 'command_durations':
      return 'command_name'
    case 'queued_order_durations':
      return 'queued_order_id'
    default:
      return 'command_name'
  }
}

const defaultSearchAttribute = findSearchAttribute('command_step_durations')

const searchOptionsForGrouping = (grouping: PageOptions['grouping']) => {
  switch (grouping) {
    case 'command_durations':
      return commandSearchOptions
    case 'queued_order_durations':
      return queuedOrderSearchOptions
    default:
      return commandStepSearchOptions
  }
}

// Shared by the data request and the export so the file cannot be filtered differently from the table.
const requestParams = (options: Pick<PageOptions, 'grouping' | 'searchValue' | 'timeFrom' | 'timeUntil'> & { searchAttribute: string }) => ({
  view: options.grouping,
  search_attribute: options.searchAttribute,
  search_value: options.searchValue,
  time_from: options.timeFrom ? options.timeFrom.toISOString() : null,
  time_until: options.timeUntil ? options.timeUntil.toISOString() : null
})

const validGrouping = (value: string | null): PageOptions['grouping'] => {
  switch (value) {
    case 'command_durations':
    case 'queued_order_durations':
      return value
    default:
      return 'command_step_durations'
  }
}

const CommandStepTable = ({ commandHistory, handlePageChange, currentPage }) => {
  const localization = useContext(LocalizationContext)
  const tenantTz = useTenantTimezone()

  return (
    <>
      <table className='table table-robis'>
        <thead className='thead-robis'>
          <tr>
            <th>{t('command_name', { scope })}</th>
            <th>{t('step_name', { scope })}</th>
            <th>{t('queued_order_id', { scope })}</th>
            <th>{t('estimated_duration', { scope })}</th>
            <th>{t('actual_duration', { scope })}</th>
            <th>{t('time_difference', { scope })}</th>
            <th>{t('executed_at', { scope })}</th>
            <th>{t('time_since_last_command', { scope })}</th>
          </tr>
        </thead>
        <tbody>
          {map(commandHistory.durations, (commandStep, index) =>
          (
            <tr key={`${commandStep.step_name}-${index}`}>
              <td>{commandStep.command_name}</td>
              <td>{commandStep.step_name}</td>
              <td>{commandStep.queued_order_id}</td>
              <td>{commandStep.estimated_duration}</td>
              <td>{commandStep.actual_duration}</td>
              <td>{commandStep.time_difference}</td>
              <td title={timeSourceTitle(commandStep.executed_at, TIME_SOURCE.robot)}>
                {commandStep.executed_at ? moment.tz(commandStep.executed_at, tenantTz).format(`${localization.date_format} ${localization.time_format}.SSS`) : '-'}
              </td>
              <td>{commandStep.time_since_last_command != null ? Number(commandStep.time_since_last_command).toFixed(3) : '-'}</td>
            </tr>
          ))
          }
        </tbody>
      </table>

      <Pagination
        count={commandHistory.totalPages}
        size='large'
        page={currentPage}
        variant='outlined'
        shape='rounded'
        onChange={(e, page) => handlePageChange(page)}
      />
    </>
  )
}

const CommandTable = ({ commandHistory, handlePageChange, currentPage }) => {
  return (
    <>
      <table className='table table-robis'>
        <thead className='thead-robis'>
          <tr>
            <th>{t('command_name', { scope })}</th>
            <th>{t('queued_order_id', { scope })}</th>
            <th>{t('estimated_duration', { scope })}</th>
            <th>{t('actual_duration', { scope })}</th>
            <th>{t('time_difference', { scope })}</th>
          </tr>
        </thead>
        <tbody>
          {map(commandHistory.durations, (commandStep, index) =>
          (
            <tr key={`${commandStep.command_name}-${index}`}>
              <td>{commandStep.command_name}</td>
              <td>{commandStep.queued_order_id}</td>
              <td>{commandStep.estimated_duration}</td>
              <td>{commandStep.actual_duration}</td>
              <td>{commandStep.time_difference}</td>
            </tr>
          ))
          }
        </tbody>
      </table>
      <Pagination
        count={commandHistory.totalPages}
        size='large'
        page={currentPage}
        variant='outlined'
        shape='rounded'
        onChange={(e, page) => handlePageChange(page)}
      />
    </>
  )
}

const QueuedOrderTable = ({ commandHistory, handlePageChange, currentPage }) => {

  return (
    <>
      <table className='table table-robis'>
        <thead className='thead-robis'>
          <tr>
            <th>{t('queued_order_id', { scope })}</th>
            <th>{t('recipe_name', { scope })}</th>
            <th>{t('estimated_duration', { scope })}</th>
            <th>{t('actual_duration', { scope })}</th>
            <th>{t('time_difference', { scope })}</th>
          </tr>
        </thead>
        <tbody>
          {map(commandHistory.durations, (commandStep, index) =>
          (
            <tr key={`${commandStep.queued_order_id}-${index}`}>
              <td>{commandStep.queued_order_id}</td>
              <td>{commandStep.recipe_name}</td>
              <td>{commandStep.estimated_duration}</td>
              <td>{commandStep.actual_duration}</td>
              <td>{commandStep.time_difference}</td>
            </tr>
          ))
          }
        </tbody>
      </table>
      <Pagination
        count={commandHistory.totalPages}
        size='large'
        page={currentPage}
        variant='outlined'
        shape='rounded'
        onChange={(e, page) => handlePageChange(page)}
      />
    </>
  )
}

interface AvailableCommandStep {
  step_name: string
  step_id: number
}

interface AvailableCommand {
  command_name: string
  command_id: number
  command_steps: AvailableCommandStep[]
}

const suggestionsExtractors: Partial<Record<string, (commands: AvailableCommand[]) => string[]>> = {
  command_name: (commands) => commands.map((command) => command.command_name),
  step_name: (commands) => commands.flatMap((command) => command.command_steps.map((step) => step.step_name))
}

interface CommandHistoryPayload {
  durations: unknown[]
  totalPages: number
}

const EMPTY_HISTORY: CommandHistoryPayload = { durations: [], totalPages: 0 }

const CommandTableView = () => {

  type GroupingOption = 'command_step_durations' | 'command_durations' | 'queued_order_durations'

  const availableGroupingOptions = [
    {
    'label': t('command_steps', { scope }),
    'value': 'command_step_durations' as GroupingOption
    },
    {
      'label': t('commands', { scope }),
      'value': 'command_durations' as GroupingOption
    },
    {
      'label': t('queued_orders', { scope }),
      'value': 'queued_order_durations' as GroupingOption
    }
]

  const tenantTz = useTenantTimezone()

  // Mirrors Reports::CommandStepDurationReport.apply_time_filter's silent 30-day default (not applied to queued_order_durations).
  const defaultTimeFromFor = (grouping: PageOptions['grouping']) =>
    grouping === 'queued_order_durations' ? null : tenantNow(tenantTz).subtract(30, 'days')

  const [commandDurations, setCommandDurations] = useState<CommandHistoryPayload>(EMPTY_HISTORY)
  const [error, setError] = useState<string | null>(null)
  const [pageOptions, setPageOptions] = useState(() => {
    const grouping = validGrouping(getParam('grouping'))
    const urlTimeFrom = parseIsoParam(getParam('time_from'))
    const urlTimeUntil = parseIsoParam(getParam('time_until'))
    const shouldAutoDefaultTimeFrom = !urlTimeFrom && !urlTimeUntil

    return {
      ...parseSearchParams<CommandStepSearchOption>(searchOptionsForGrouping(grouping), findSearchAttribute(grouping)),
      currentPage: parsePageParam(getParam('page')),
      grouping,
      timeFrom: urlTimeFrom || (shouldAutoDefaultTimeFrom ? defaultTimeFromFor(grouping) : null),
      timeFromIsAutoDefault: shouldAutoDefaultTimeFrom,
      timeUntil: urlTimeUntil
    }
  })

  const searchRef = useRef<HTMLInputElement>(null);

  const [availableCommands, setAvailableCommands] = useState<AvailableCommand[]>([])
  const [searchInputValue, setSearchInputValue] = useState(pageOptions.searchValue)

  useEffect(() => {
    axios.get<AvailableCommand[]>(available_commands_and_steps_admin_command_step_durations_path()).then(({ data }) => {
      setAvailableCommands(data)
    })
  }, [])

  const searchSuggestions = useMemo(() => {
    const extractSuggestions = suggestionsExtractors[pageOptions.searchAttribute]
    return extractSuggestions ? uniq(extractSuggestions(availableCommands)) : null
  }, [availableCommands, pageOptions.searchAttribute])

  useEffect(() => {
    applyParams({
      grouping: pageOptions.grouping === 'command_step_durations' ? null : pageOptions.grouping,
      search_attribute: pageOptions.searchAttribute === findSearchAttribute(pageOptions.grouping) ? null : pageOptions.searchAttribute,
      search_value: pageOptions.searchValue || null,
      page: pageOptions.currentPage > 1 ? String(pageOptions.currentPage) : null,
      time_from: pageOptions.timeFrom ? pageOptions.timeFrom.toISOString() : null,
      time_until: pageOptions.timeUntil ? pageOptions.timeUntil.toISOString() : null
    }, 'replace')

    // Aborting on every change keeps a slow response for an older search from overwriting a newer one.
    const controller = new AbortController()
    setError(null)
    axios.get<CommandHistoryPayload>('/admin/command_step_durations', {
      signal: controller.signal,
      params: {
        ...requestParams(pageOptions),
        page: pageOptions.currentPage,
        format: 'json'
      }})
      .then(({ data }) => setCommandDurations(data))
      .catch((err) => {
        if (axios.isCancel(err)) return
        console.error('Failed to load command history:', err)
        // Only a rejected search answers with a readable message; any other failure body may be an HTML error page.
        setError(err?.response?.status === 422 ? err.response.data : t('load_failed', { scope }))
        setCommandDurations(EMPTY_HISTORY)
      })
    return () => controller.abort()
  }, [pageOptions])

  const handlePageChange = (page) => {
    setPageOptions(prevOptions => ({ ...prevOptions, currentPage: page }))
  }

  const handleSearch = useCallback(debounce((newSearchValue: any) => {
    setPageOptions(prevOptions => ({ ...prevOptions, searchValue: newSearchValue, currentPage: 1 }))

  }, 300), [])

  const handleSuggestedSearchInputChange = (newSearchValue: string) => {
    setSearchInputValue(newSearchValue)
    handleSearch(newSearchValue)
  }

  const handleSearchOptionChange = (newSearchOption) => {
    setPageOptions(prevOptions => ({ ...prevOptions, searchAttribute: newSearchOption, searchValue: ''}))
    clearSearchField()
    setSearchInputValue('')
  }

  const handleTimeFromChange = (time) => {
    setPageOptions(prevOptions => ({ ...prevOptions, timeFrom: time, timeFromIsAutoDefault: false }))
  }

  const handleTimeUntilChange = (time) => {
    setPageOptions(prevOptions => ({ ...prevOptions, timeUntil: time }))
  }

  const handleGroupingChange = (newGrouping) => {
    setPageOptions(prevOptions => ({
      ...prevOptions,
      grouping: newGrouping,
      searchAttribute: findSearchAttribute(newGrouping),
      searchValue: '',
      currentPage: 1,
      timeFrom: prevOptions.timeFromIsAutoDefault ? defaultTimeFromFor(newGrouping) : prevOptions.timeFrom,
    }))

    clearSearchField()
    setSearchInputValue('')
  }

  const clearSearchField = () => {
    handleSearch.cancel()
    // The Autocomplete branch is controlled by searchInputValue and ignores searchRef, which is
    // only attached to the plain input rendered when the attribute has no suggestions.
    setSearchInputValue('')
    if (searchRef.current) {
      searchRef.current.value = '';
    }
  };

  const availableSearchOptions = () => searchOptionsForGrouping(pageOptions.grouping)

  return (
    <div className="command-history-table">
      <div className="command-filter-bar">
        <div className="command-filter command-filter-select">
          <span className="command-filter-label">{t('group_data_by', { scope })}</span>
          <Select
            className='react-select'
            value={pageOptions.grouping}
            onChange={(opt) => handleGroupingChange(opt.target.value as GroupingOption)}
            input={<OutlinedInput />}
            MenuProps={{ PaperProps: { style: { maxHeight: 224, width: 250 } } }}
          >
            {availableGroupingOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                <ListItemText primary={option.label} />
              </MenuItem>
            ))}
          </Select>
        </div>

        <div className="command-filter command-filter-select">
          <span className="command-filter-label">{t('search_by', { scope })}</span>
          <Select
            className='react-select'
            value={pageOptions.searchAttribute}
            onChange={(opt) => handleSearchOptionChange(opt.target.value)}
            input={<OutlinedInput />}
            MenuProps={{ PaperProps: { style: { maxHeight: 224, width: 250 } } }}
          >
            {availableSearchOptions().map((option) => (
              <MenuItem key={option.value} value={option.value}>
                <ListItemText primary={option.label} />
              </MenuItem>
            ))}
          </Select>
        </div>

        <div className="command-filter command-filter-search">
          <span className="command-filter-label">{t('search', { scope })}</span>
          {searchSuggestions ? (
            <Autocomplete
              freeSolo
              autoFocus
              options={searchSuggestions}
              inputValue={searchInputValue}
              onInputChange={(_e, newValue) => handleSuggestedSearchInputChange(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant='standard'
                  InputProps={{ ...params.InputProps, disableUnderline: true, className: 'search-input form-control' }}
                  placeholder={`${t('search_by', { scope })} ${lowerFirst(t(pageOptions.searchAttribute, { scope }))}...`}
                />
              )}
            />
          ) : (
            <input type='search' autoFocus
              name='q'
              className='search-input form-control'
              autoComplete='off'
              placeholder={`${t('search_by', { scope })} ${lowerFirst(t(pageOptions.searchAttribute, { scope }))}...`}
              defaultValue={pageOptions.searchValue}
              onChange={(e) => handleSearch(e.target.value)}
              ref={searchRef}
            />
          )}
        </div>

        <div className="command-filter command-filter-dates">
          <span className="command-filter-label">{t('date_range', { scope })}</span>
          <div className="time-filter">
            <TenantDateTimePicker
              className='me-2'
              disableFuture
              label={!!pageOptions.timeFrom ? '' : t('from', { scope })}
              value={pageOptions.timeFrom}
              onChange={handleTimeFromChange}
            />
            <TenantDateTimePicker
              disableFuture
              label={!!pageOptions.timeUntil ? '' : t('to', { scope })}
              value={pageOptions.timeUntil}
              onChange={handleTimeUntilChange}
            />
          </div>
        </div>

        <div className="command-filter command-filter-export">
          <ExportToExcelButton params={requestParams(pageOptions)} />
        </div>
      </div>

      {error && (
        <div className='alert alert-danger' role='alert'>
          {error}
        </div>
      )}

      {pageOptions.grouping == 'command_step_durations' &&
        <CommandStepTable
          commandHistory={commandDurations}
          currentPage={pageOptions.currentPage}
          handlePageChange={handlePageChange}
        />
      }
      {pageOptions.grouping == 'command_durations' &&
        <CommandTable
          commandHistory={commandDurations}
          currentPage={pageOptions.currentPage}
          handlePageChange={handlePageChange}
        />
      }
      {pageOptions.grouping == 'queued_order_durations' &&
        <QueuedOrderTable
          commandHistory={commandDurations}
          currentPage={pageOptions.currentPage}
          handlePageChange={handlePageChange}
        />
      }
    </div>
  )
}

export default CommandTableView
